import { createBrowserClient } from "@supabase/ssr";

import type { Database } from "./types";

const isBrowser = typeof window !== "undefined";

// AIDEV-NOTE: This module is also evaluated during SSR (via protected-route.tsx), where
// `process` exists — but in the browser bundle it does not. Reading `process.env` directly
// throws ReferenceError whenever a key is absent from window.env (JSON.stringify drops
// undefined values), which white-screens the app. Resolve it once, guarded.
const serverEnv = typeof process !== "undefined" ? process.env : undefined;

// AIDEV-NOTE: Support runtime env vars via window.env for deployment flexibility
const resolvedUrl =
    (isBrowser && window.env?.SUPABASE_URL) || serverEnv?.SUPABASE_URL;

const resolvedKey =
    (isBrowser && window.env?.SUPABASE_ANON_PUBLIC) ||
    serverEnv?.SUPABASE_ANON_PUBLIC;

// AIDEV-NOTE: Region migration. Pinned auth cookie name; must match ssr.server.ts.
// Optional by design — absence falls back to Supabase's default cookie name.
const supabaseAuthCookieName =
    (isBrowser && window.env?.SUPABASE_AUTH_COOKIE_NAME) ||
    serverEnv?.SUPABASE_AUTH_COOKIE_NAME ||
    undefined;

if (!resolvedUrl) {
    throw new Error("Missing Supabase URL environment variable");
}
if (!resolvedKey) {
    throw new Error("Missing Supabase Key environment variable");
}

// AIDEV-NOTE: Exported after the guards so both stay `string`, not `string | undefined`.
export const supabaseUrl = resolvedUrl;
export const supabaseKey = resolvedKey;

function isStorageWritable() {
    try {
        localStorage.setItem("__t", "1");
        localStorage.removeItem("__t");
        return true;
    } catch {
        return false;
    }
}

// In-memory fallback that fully implements the Storage interface
function createMemoryStorage(): Storage {
    let store: Record<string, string> = {};
    return {
        get length() {
            return Object.keys(store).length;
        },
        clear() {
            store = {};
        },
        getItem(key: string) {
            return store.hasOwnProperty(key) ? store[key] : null;
        },
        key(index: number) {
            return Object.keys(store)[index] ?? null;
        },
        removeItem(key: string) {
            delete store[key];
        },
        setItem(key: string, value: string) {
            store[key] = value;
        },
    } as Storage;
}

const isWritable = isStorageWritable();
const safeStorage = isWritable ? localStorage : createMemoryStorage();

// Monkey-patch the real localStorage so that any *other* modules that haven’t
// been updated yet won’t crash when they call setItem in Private-Browsing.
if (!isWritable) {
    try {
        const noop = () => {};
        localStorage.setItem = noop as typeof localStorage.setItem;
        localStorage.removeItem = noop as typeof localStorage.removeItem;
        localStorage.clear = noop as typeof localStorage.clear;
    } catch {
        /* ignore – cannot redefine, but that’s fine */
    }
}

export const supabase = createBrowserClient(supabaseUrl, supabaseKey, {
    ...(supabaseAuthCookieName
        ? { cookieOptions: { name: supabaseAuthCookieName } }
        : {}),
    auth: {
        storage: safeStorage,
        persistSession: isWritable,
    },
});

type AuthClientWithSuppression = typeof supabase.auth & {
    suppressGetSessionWarning?: boolean;
};

const authClient = supabase.auth as AuthClientWithSuppression;
// AIDEV-NOTE: Supabase warns when reading SSR session.user; suppress noisy console output.
if ("suppressGetSessionWarning" in authClient) {
    authClient.suppressGetSessionWarning = true;
}
// AIDEV-NOTE: SSR-enabled browser client that syncs with server cookies
